import React, { useEffect, useRef, useState } from 'react';

/* ─── DEMO PREVIEWS ─────────────────────────────
   Shared by the demo results (BusinessForm) and the order page. */

const PREVIEW_WIDTH = 1280; // demos render at desktop width, then get scaled down to fit

// In a srcdoc iframe, "#section" links resolve against this site's URL, so clicking one
// navigated the preview to draculadev.com. Scroll to the section inside the demo instead.
const IN_PAGE_LINKS_SCRIPT = `<script>document.addEventListener('click',function(e){
var a=e.target.closest&&e.target.closest('a[href^="#"]');if(!a)return;e.preventDefault();
var id=a.getAttribute('href').slice(1),t=id&&document.getElementById(id);
if(t)t.scrollIntoView({behavior:'smooth'});else window.scrollTo({top:0,behavior:'smooth'});});</script>`;

export const withInPageLinks = (html = '') =>
  html.includes('</body>') ? html.replace('</body>', IN_PAGE_LINKS_SCRIPT + '</body>') : html + IN_PAGE_LINKS_SCRIPT;

/* Live, non-interactive thumbnail that fills its (position: relative) parent */
export const DemoThumbnail = ({ html, title }) => {
  const ref = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([e]) => setSize({ w: e.contentRect.width, h: e.contentRect.height }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const scale = size.w / PREVIEW_WIDTH;

  return (
    <div ref={ref} style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
      {scale > 0 && (
        <iframe
          srcDoc={html}
          title={title}
          sandbox="allow-scripts"
          tabIndex={-1}
          loading="lazy"
          style={{
            position: 'absolute', top: 0, left: 0,
            width: PREVIEW_WIDTH, height: size.h / scale,
            border: 'none', pointerEvents: 'none',
            transform: `scale(${scale})`, transformOrigin: 'top left',
          }}
        />
      )}
    </div>
  );
};

/* Full-screen preview with an optional call-to-action (e.g. "Comandă acest site") */
export const FullscreenPreview = ({ title, badge, html, onClose, actionLabel, onAction }) => (
  <div style={{
    position: 'fixed', inset: 0, zIndex: 300,
    background: '#000', display: 'flex', flexDirection: 'column',
  }}>
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '10px clamp(12px,4vw,20px)', background: '#0d0e12',
      borderBottom: '1px solid #1e2030', gap: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        <span style={{ fontSize: 18 }}>🧛</span>
        <span style={{
          color: '#f8f8f2', fontWeight: 600, fontSize: 14,
          fontFamily: "'Space Grotesk', sans-serif",
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{title}</span>
        {badge && (
          <span style={{
            background: '#50fa7b15', color: '#50fa7b', flexShrink: 0,
            padding: '2px 10px', borderRadius: 10, fontSize: 10, fontWeight: 600,
            fontFamily: "'JetBrains Mono', monospace",
          }}>{badge}</span>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        {actionLabel && (
          <button onClick={onAction} style={{
            background: 'linear-gradient(135deg, #50fa7b, #8be9fd)', border: 'none', borderRadius: 8,
            padding: '6px 16px', color: '#0d0e12', fontWeight: 700,
            cursor: 'pointer', fontSize: 12, fontFamily: "'Space Grotesk', sans-serif", whiteSpace: 'nowrap',
          }}>{actionLabel}</button>
        )}
        <button onClick={onClose} style={{
          background: '#ff555520', border: '1px solid #ff555540', borderRadius: 8,
          padding: '6px 16px', color: '#ff5555', fontWeight: 600,
          cursor: 'pointer', fontSize: 12, fontFamily: "'Space Grotesk', sans-serif",
          transition: 'all 0.2s', whiteSpace: 'nowrap',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = '#ff5555'; e.currentTarget.style.color = '#fff'; }}
        onMouseLeave={e => { e.currentTarget.style.background = '#ff555520'; e.currentTarget.style.color = '#ff5555'; }}>
          ✕ Închide
        </button>
      </div>
    </div>
    <iframe
      srcDoc={withInPageLinks(html)}
      title={title}
      style={{ flex: 1, border: 'none', width: '100%' }}
      sandbox="allow-scripts"
    />
  </div>
);
